import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ImageSourcePropType,
} from 'react-native';
import PlatformIcon from '../common/PlatformIcon';

const MODULE_COLOR = '#F97316';

export interface MemberItem {
  user_id: string;
  profile?: {
    display_name?: string | null;
    avatar_url?: string | null;
  } | null;
}

export interface MembersSectionProps {
  members: MemberItem[];
  creatorId?: string;
  isCreator?: boolean;
  onInvitePress?: () => void;
  getAvatarSource?: (url: string) => ImageSourcePropType | null;
}

const MembersSection: React.FC<MembersSectionProps> = ({
  members,
  creatorId,
  isCreator = false,
  onInvitePress,
  getAvatarSource,
}) => {
  if (!members || members.length === 0) {
    return null;
  }

  const renderAvatar = (member: MemberItem) => {
    const avatarUrl = member.profile?.avatar_url;
    const displayName = member.profile?.display_name || 'U';
    const initial = displayName.charAt(0).toUpperCase();

    if (avatarUrl && getAvatarSource) {
      const source = getAvatarSource(avatarUrl);
      if (source) {
        return <Image source={source} style={styles.memberGridAvatar} />;
      }
    }

    return (
      <View style={styles.memberGridAvatarPlaceholder}>
        <Text style={styles.memberGridAvatarText}>{initial}</Text>
      </View>
    );
  };

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <View style={styles.sectionTitleRow}>
          <PlatformIcon name="group" size={18} color={MODULE_COLOR} family="MaterialIcons" />
          <Text style={styles.sectionTitle}>成员 ({members.length})</Text>
        </View>
        {isCreator && onInvitePress && (
          <TouchableOpacity style={styles.addButton} onPress={onInvitePress}>
            <PlatformIcon name="person-add" size={14} color={MODULE_COLOR} family="MaterialIcons" />
            <Text style={styles.addButtonText}>邀请</Text>
          </TouchableOpacity>
        )}
      </View>
      <View style={styles.membersGrid}>
        {members.map((member) => (
          <View key={member.user_id} style={styles.memberGridItem}>
            {renderAvatar(member)}
            {member.user_id === creatorId && (
              <View style={styles.creatorBadge}>
                <PlatformIcon name="star" size={10} color="#FFD700" family="MaterialIcons" />
              </View>
            )}
            <Text style={styles.memberGridName} numberOfLines={1}>
              {member.profile?.display_name || '未知'}
            </Text>
            <Text style={styles.memberGridRole}>
              {member.user_id === creatorId ? '创建人' : '成员'}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1F2937',
    marginLeft: 6,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF7ED',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: MODULE_COLOR,
  },
  addButtonText: {
    color: MODULE_COLOR,
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  membersGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  memberGridItem: {
    width: '33.33%',
    alignItems: 'center',
    paddingVertical: 12,
    position: 'relative',
  },
  memberGridAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  memberGridAvatarPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: MODULE_COLOR,
    justifyContent: 'center',
    alignItems: 'center',
  },
  memberGridAvatarText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
  },
  creatorBadge: {
    position: 'absolute',
    top: 8,
    right: '30%',
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 2,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  memberGridName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1F2937',
    marginTop: 6,
    textAlign: 'center',
  },
  memberGridRole: {
    fontSize: 11,
    color: '#9CA3AF',
    marginTop: 2,
  },
});

export default MembersSection;
